import { SetStateAction, useEffect, useRef } from "react";
import { OfferCardType } from "../../../../../services/redux/types/CartLoyaltyV2Types";
import { BundleOfferImages } from "../../ItemList/bundleItem/bundleItemImage";
import styles from "./GraphicsSlider.module.scss";

const GraphicsSlider = ({
  currentIndex,
  setCurrentIndex,
  offerCards,
  large,
  flipGraphicColors,
}: {
  currentIndex: number;
  setCurrentIndex: (value: SetStateAction<number>) => void;
  offerCards: OfferCardType[];
  large?: boolean;
  flipGraphicColors?: boolean;
}) => {
  const AUTO_SCROLL_INTERVAL = 4000;
  const RESUME_SCROLL_TIMEOUT = 1000;

  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoScrolling = useRef(false);
  const scrollEndTimeout = useRef<NodeJS.Timeout | null>(null);

  const scrollToIndex = (index: number) => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.clientWidth * index;
      const currentScroll = containerRef.current.scrollLeft;
      const diff = Math.abs(currentScroll - scrollAmount);

      if (diff < 1) {
        return;
      } // already close enough

      isAutoScrolling.current = true;
      containerRef.current.scrollTo({
        left: scrollAmount,
        behavior: "smooth",
      });
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 500);
    }
  };

  const handleScroll = () => {
    if (isAutoScrolling.current) {
      return;
    }

    if (scrollEndTimeout.current) {
      clearTimeout(scrollEndTimeout.current);
    }

    scrollEndTimeout.current = setTimeout(() => {
      if (containerRef.current) {
        const { scrollLeft, clientWidth } = containerRef.current;
        const newIndex = Math.round(scrollLeft / clientWidth);

        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
        }
      }
    }, 20); // wait for 20ms of no scroll to assume "scroll ended"
  };

  const startAutoScroll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % offerCards.length);
    }, AUTO_SCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleUserInteraction = () => {
    stopAutoScroll();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      startAutoScroll();
    }, RESUME_SCROLL_TIMEOUT);
  };

  useEffect(() => {
    startAutoScroll();

    return () => {
      stopAutoScroll();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [offerCards.length]);

  useEffect(() => {
    scrollToIndex(currentIndex);
  }, [currentIndex]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      onTouchStart={handleUserInteraction}
      onTouchEnd={handleUserInteraction}
      onMouseDown={handleUserInteraction}
      onMouseUp={handleUserInteraction}
      className={styles.scrollable_wrapper}
    >
      {offerCards?.map((item, index) => (
        <div key={index} className={styles.scrollable_item}>
          <BundleOfferImages
            graphics={item.graphics}
            transparentBg
            large={large}
            customScale={large ? 1.75 : undefined}
            flipGraphicColors={large || flipGraphicColors}
          />
        </div>
      ))}
    </div>
  );
};

export default GraphicsSlider;
